// Services do painel admin de White Labels (superAdmin) — queries de
// leitura (wave 1) + mutations de edição (wave 2: PUT dados/tema,
// upsert de configurações/relatórios e CRUD de métodos). Molde
// services/admin/reprocess.ts: queryOptions exportadas p/ teste,
// gcTime 0 no que é dado de configuração (nunca renderizar cache
// velho), placeholderData undefined anulando o keepPreviousData
// global, invalidação por predicate e toasts com getErrorMessage.
import { api } from "@/api"
import { BASE_URL, ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import { getApiErrorMessage } from "@/lib/api-error"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@/libs"
import type {
  AdminWhiteLabelConfigurations,
  AdminWhiteLabelCreatePayload,
  AdminWhiteLabelDetail,
  AdminWhiteLabelInsuranceType,
  AdminWhiteLabelInsuranceTypePayload,
  AdminWhiteLabelListFilters,
  AdminWhiteLabelListResponse,
  AdminWhiteLabelPaymentMethod,
  AdminWhiteLabelReportConfiguration,
  AdminWhiteLabelUpdatePayload,
} from "@/types/admin-white-labels"

// Chave base das queries da feature — usada também na invalidação.
export const ADMIN_WHITE_LABELS_QUERY_KEY =
  ENDPOINTS.ADMIN_WHITE_LABELS

// Resolve o path de detalhe trocando o placeholder {{uuid}}.
const resolveUuidPath = (
  template: string,
  uuid: string
): string => template.replace("{{uuid}}", uuid)

// True quando o erro normalizado do api (ApiHelper.handleError) é 404.
const isNotFoundError = (error: unknown): boolean =>
  Boolean(
    error &&
    typeof error === "object" &&
    "statusCode" in error &&
    (error as { statusCode: unknown }).statusCode === 404
  )

// Lista paginada (GET /white-labels) — busca server-side por `name`;
// o refinamento por slug/loja é client-side (utils da feature).
export const getAdminWhiteLabels = async (
  filters: AdminWhiteLabelListFilters = {}
): Promise<AdminWhiteLabelListResponse> => {
  const params = new URLSearchParams()
  if (filters.name) params.set("name", filters.name)
  if (filters.page !== undefined)
    params.set("page", String(filters.page))
  if (filters.quantity !== undefined)
    params.set("quantity", String(filters.quantity))
  const queryString = params.toString()
  const url = `${BASE_URL}${ENDPOINTS.ADMIN_WHITE_LABELS}${queryString ? `?${queryString}` : ""}`
  const { data } =
    await api.get<AdminWhiteLabelListResponse>(url)
  return data
}

// Detalhe (GET /white-labels/{uuid}).
export const getAdminWhiteLabel = async (
  uuid: string
): Promise<AdminWhiteLabelDetail> => {
  const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL, uuid)}`
  const { data } = await api.get<AdminWhiteLabelDetail>(url)
  return data
}

// Configurações de negócio (GET .../configurations) — 404 vira
// `null` (white label ainda sem configuração; o PUT cria — upsert).
export const getAdminWhiteLabelConfigurations = async (
  uuid: string
): Promise<AdminWhiteLabelConfigurations | null> => {
  const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_CONFIGURATIONS, uuid)}`
  try {
    const { data } =
      await api.get<AdminWhiteLabelConfigurations>(url)
    return data
  } catch (error) {
    if (isNotFoundError(error)) return null
    throw error
  }
}

// Métodos CADASTRADOS no WL (GET /white-labels/{uuid}/payment-methods —
// API do backoffice, só exposta no gateway). A resposta é o conteúdo de
// white_label_payment_method: o catálogo do que ainda dá para cadastrar vem
// de outra query (usePaymentMethodCatalog).
export const getAdminWhiteLabelPaymentMethods = async (
  uuid: string
): Promise<AdminWhiteLabelPaymentMethod[]> => {
  const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_PAYMENT_METHODS, uuid)}`
  const { data } = await api.get<
    | AdminWhiteLabelPaymentMethod[]
    | { paymentMethods?: AdminWhiteLabelPaymentMethod[] }
  >(url)
  // O backoffice passou a devolver um ENVELOPE ({ paymentMethods: [...] })
  // porque a lista nua vazia virava 204 sem corpo no gateway — e o axios
  // entrega corpo vazio como string, que não é nullish e estourava no `.map`.
  //
  // Aceita os dois formatos de propósito: gateway e painel sobem em momentos
  // diferentes, então exigir o envelope faria a janela entre um deploy e outro
  // esvaziar a aba sem erro visível.
  if (Array.isArray(data)) return data
  return Array.isArray(data?.paymentMethods)
    ? data.paymentMethods
    : []
}

// Report configuration (GET .../report-configuration) — 404 vira
// `null` ("não configurado" é estado esperado do domínio, §3).
export const getAdminWhiteLabelReportConfiguration = async (
  uuid: string
): Promise<AdminWhiteLabelReportConfiguration | null> => {
  const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_REPORT_CONFIGURATION, uuid)}`
  try {
    const { data } =
      await api.get<AdminWhiteLabelReportConfiguration>(url)
    return data
  } catch (error) {
    if (isNotFoundError(error)) return null
    throw error
  }
}

// Tipos de seguro (GET .../insurance-types) — o core devolve o catálogo
// inteiro já interpolado (rótulo em vigor + padrão do enum), então não há
// estado "não configurado": white label sem nada retorna todos com
// source ENUM.
export const getAdminWhiteLabelInsuranceTypes = async (
  uuid: string
): Promise<AdminWhiteLabelInsuranceType[]> => {
  const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_INSURANCE_TYPES, uuid)}`
  const { data } =
    await api.get<AdminWhiteLabelInsuranceType[]>(url)
  // Mesma armadilha do 204 (corpo vazio chega como string).
  return Array.isArray(data) ? data : []
}

// Overrides compartilhados: dado de configuração admin nunca
// renderiza de cache velho ao reentrar (mesmo racional do reprocess).
const freshQueryOverrides = {
  refetchOnMount: "always" as const,
  gcTime: 0,
  placeholderData: undefined,
}

// Opções das queries (exportadas p/ teste).
export const adminWhiteLabelsQueryOptions = (
  filters: AdminWhiteLabelListFilters = {}
) => ({
  queryKey: [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "list",
    filters,
  ] as const,
  queryFn: async () => getAdminWhiteLabels(filters),
  ...freshQueryOverrides,
})

export const adminWhiteLabelDetailQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "detail",
    uuid,
  ] as const,
  queryFn: async () => getAdminWhiteLabel(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

export const adminWhiteLabelConfigurationsQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "configurations",
    uuid,
  ] as const,
  queryFn: async () =>
    getAdminWhiteLabelConfigurations(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

export const adminWhiteLabelPaymentMethodsQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "payment-methods",
    uuid,
  ] as const,
  queryFn: async () =>
    getAdminWhiteLabelPaymentMethods(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

export const adminWhiteLabelReportConfigurationQueryOptions =
  (uuid: string) => ({
    queryKey: [
      ADMIN_WHITE_LABELS_QUERY_KEY,
      "report-configuration",
      uuid,
    ] as const,
    queryFn: async () =>
      getAdminWhiteLabelReportConfiguration(uuid),
    enabled: Boolean(uuid),
    ...freshQueryOverrides,
  })

export const adminWhiteLabelInsuranceTypesQueryOptions = (
  uuid: string
) => ({
  queryKey: [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "insurance-types",
    uuid,
  ] as const,
  queryFn: async () =>
    getAdminWhiteLabelInsuranceTypes(uuid),
  enabled: Boolean(uuid),
  ...freshQueryOverrides,
})

// Hooks de leitura.
export const useAdminWhiteLabels = (
  filters: AdminWhiteLabelListFilters = {}
) => useQuery(adminWhiteLabelsQueryOptions(filters))

export const useAdminWhiteLabel = (uuid: string) =>
  useQuery(adminWhiteLabelDetailQueryOptions(uuid))

export const useAdminWhiteLabelConfigurations = (
  uuid: string
) =>
  useQuery(adminWhiteLabelConfigurationsQueryOptions(uuid))

export const useAdminWhiteLabelPaymentMethods = (
  uuid: string
) =>
  useQuery(adminWhiteLabelPaymentMethodsQueryOptions(uuid))

export const useAdminWhiteLabelReportConfiguration = (
  uuid: string
) =>
  useQuery(
    adminWhiteLabelReportConfigurationQueryOptions(uuid)
  )

export const useAdminWhiteLabelInsuranceTypes = (
  uuid: string
) =>
  useQuery(adminWhiteLabelInsuranceTypesQueryOptions(uuid))

// ── Mutations (wave 2 — edição) ─────────────────────────────────────

// Invalida TODAS as queries da feature (lista + detalhe + tabs) —
// consolida o estado do servidor após qualquer escrita.
const useInvalidateAdminWhiteLabels = () => {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === ADMIN_WHITE_LABELS_QUERY_KEY,
    })
  }
}

// PUT /white-labels/{uuid} — dados + tema (cores/logo/active).
// `logo` ausente = mantém o atual; cores null = mantém (merge).
export const useUpdateAdminWhiteLabelMutation = (
  uuid: string
) => {
  const invalidate = useInvalidateAdminWhiteLabels()

  return useMutation<
    AdminWhiteLabelDetail,
    unknown,
    AdminWhiteLabelUpdatePayload
  >({
    mutationFn: async (payload) => {
      const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL, uuid)}`
      const { data } = await api.put<AdminWhiteLabelDetail>(
        url,
        payload
      )
      return data
    },
    onSuccess: async () => {
      toast({
        title: "White label atualizado",
        variant: "success",
      })
      await invalidate()
    },
    onError: (error) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Erro ao atualizar o white label"
        ),
        variant: "destructive",
      })
    },
  })
}

// PUT /white-labels/{uuid}/configurations — upsert único das ~30
// configurações de negócio (cria quando o WL ainda não tem).
export const useUpsertAdminWhiteLabelConfigurationsMutation =
  (uuid: string) => {
    const invalidate = useInvalidateAdminWhiteLabels()

    return useMutation<
      AdminWhiteLabelConfigurations,
      unknown,
      AdminWhiteLabelConfigurations
    >({
      mutationFn: async (payload) => {
        const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_CONFIGURATIONS, uuid)}`
        const { data } =
          await api.put<AdminWhiteLabelConfigurations>(
            url,
            payload
          )
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Configurações salvas",
          variant: "success",
        })
        await invalidate()
      },
      onError: (error) => {
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao salvar as configurações"
          ),
          variant: "destructive",
        })
      },
    })
  }

// PUT /white-labels/{uuid}/report-configuration — upsert das 2
// flags de relatório p/ corretoras (404 no GET = não configurado;
// o PUT cria).
export const useUpsertAdminWhiteLabelReportConfigurationMutation =
  (uuid: string) => {
    const invalidate = useInvalidateAdminWhiteLabels()

    return useMutation<
      AdminWhiteLabelReportConfiguration,
      unknown,
      AdminWhiteLabelReportConfiguration
    >({
      mutationFn: async (payload) => {
        const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_REPORT_CONFIGURATION, uuid)}`
        const { data } =
          await api.put<AdminWhiteLabelReportConfiguration>(
            url,
            payload
          )
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Configuração de relatórios salva",
          variant: "success",
        })
        await invalidate()
      },
      onError: (error) => {
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao salvar a configuração de relatórios"
          ),
          variant: "destructive",
        })
      },
    })
  }

// ── Tipos de seguro (rótulos por white label) ───────────────────────

// Resolve o path de um tipo específico ({{uuid}} + {{code}}).
const resolveInsuranceTypePath = (
  uuid: string,
  code: string
): string =>
  ENDPOINTS.ADMIN_WHITE_LABEL_INSURANCE_TYPE.replace(
    "{{uuid}}",
    uuid
  ).replace("{{code}}", code)

// PUT .../insurance-types/{code} — upsert do rótulo. Idempotente: o
// registro é identificado pelo par (white label, tipo).
export const useUpsertAdminWhiteLabelInsuranceTypeMutation =
  (uuid: string) => {
    const invalidate = useInvalidateAdminWhiteLabels()

    return useMutation<
      AdminWhiteLabelInsuranceType,
      unknown,
      {
        code: string
        payload: AdminWhiteLabelInsuranceTypePayload
      }
    >({
      mutationFn: async ({ code, payload }) => {
        const url = `${BASE_URL}${resolveInsuranceTypePath(uuid, code)}`
        const { data } =
          await api.put<AdminWhiteLabelInsuranceType>(
            url,
            payload
          )
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Tipo de seguro salvo",
          variant: "success",
        })
        await invalidate()
      },
      onError: (error) => {
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao salvar o tipo de seguro"
          ),
          variant: "destructive",
        })
      },
    })
  }

// DELETE .../insurance-types/{code} — remove o rótulo e o tipo volta a
// ser exibido com o padrão do enum.
export const useRemoveAdminWhiteLabelInsuranceTypeMutation =
  (uuid: string) => {
    const invalidate = useInvalidateAdminWhiteLabels()

    return useMutation<unknown, unknown, string>({
      mutationFn: async (code) => {
        const url = `${BASE_URL}${resolveInsuranceTypePath(uuid, code)}`
        const { data } = await api.delete(url)
        return data
      },
      onSuccess: async () => {
        toast({
          title: "Nome padrão restaurado",
          variant: "success",
        })
        await invalidate()
      },
      onError: (error) => {
        toast({
          title: getApiErrorMessage(
            error,
            "Erro ao restaurar o nome padrão"
          ),
          variant: "destructive",
        })
      },
    })
  }

// ── Métodos de pagamento (CRUD, wave 2) ─────────────────────────────

// Resolve o path de um método específico ({{uuid}} + {{method}}).
const resolvePaymentMethodPath = (
  uuid: string,
  method: string
): string =>
  ENDPOINTS.ADMIN_WHITE_LABEL_PAYMENT_METHOD.replace(
    "{{uuid}}",
    uuid
  ).replace("{{method}}", method)

// Chave da query de payment-methods do WL (otimismo + rollback).
const paymentMethodsQueryKey = (uuid: string) =>
  [
    ADMIN_WHITE_LABELS_QUERY_KEY,
    "payment-methods",
    uuid,
  ] as const

// Contexto de rollback das mutations de método.
type PaymentMethodsSnapshotContext = {
  previous?: AdminWhiteLabelPaymentMethod[]
}

// Fábrica das mutations otimistas sobre a lista de métodos:
// snapshot no onMutate, rollback + toast no onError, invalidação por
// predicate no onSettled (consolida o estado do servidor).
const useOptimisticPaymentMethodsMutation = <TVariables>(
  uuid: string,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  applyOptimistic: (
    cache: AdminWhiteLabelPaymentMethod[],
    variables: TVariables
  ) => AdminWhiteLabelPaymentMethod[],
  errorFallback: string
) => {
  const queryClient = useQueryClient()
  const queryKey = paymentMethodsQueryKey(uuid)

  return useMutation<
    unknown,
    unknown,
    TVariables,
    PaymentMethodsSnapshotContext
  >({
    mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<
          AdminWhiteLabelPaymentMethod[]
        >(queryKey)
      queryClient.setQueryData<
        AdminWhiteLabelPaymentMethod[]
      >(queryKey, (cache) =>
        applyOptimistic(cache ?? [], variables)
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      // Rollback do otimismo.
      queryClient.setQueryData(queryKey, context?.previous)
      toast({
        title: getApiErrorMessage(error, errorFallback),
        variant: "destructive",
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_WHITE_LABELS_QUERY_KEY,
      })
    },
  })
}

// POST .../payment-methods — habilita um método novo no WL.
export const useAddWhiteLabelPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<string>(
    uuid,
    async (method) => {
      const url = `${BASE_URL}${resolveUuidPath(ENDPOINTS.ADMIN_WHITE_LABEL_PAYMENT_METHODS, uuid)}`
      const { data } = await api.post(url, {
        paymentMethod: method,
        active: true,
      })
      return data
    },
    (cache, method) => [
      ...cache,
      { whiteLabelUuid: uuid, method, active: true },
    ],
    "Erro ao adicionar o método de pagamento"
  )

// PUT .../payment-methods/{method}?active= — liga/desliga o método.
export const useUpdateWhiteLabelPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<{
    method: string
    active: boolean
  }>(
    uuid,
    async ({ method, active }) => {
      const url = `${BASE_URL}${resolvePaymentMethodPath(uuid, method)}?active=${active}`
      const { data } = await api.put(url)
      return data
    },
    (cache, { method, active }) =>
      cache.map((item) =>
        item.method === method ? { ...item, active } : item
      ),
    "Erro ao atualizar o método de pagamento"
  )

// DELETE .../payment-methods/{method} — remove o método do WL.
export const useRemoveWhiteLabelPaymentMethodMutation = (
  uuid: string
) =>
  useOptimisticPaymentMethodsMutation<string>(
    uuid,
    async (method) => {
      const url = `${BASE_URL}${resolvePaymentMethodPath(uuid, method)}`
      const { data } = await api.delete(url)
      return data
    },
    (cache, method) =>
      cache.filter((item) => item.method !== method),
    "Erro ao remover o método de pagamento"
  )

/**
 * POST /white-labels — cria o WL do merchant logado.
 *
 * O gateway resolve o storeId a partir do token; o front só manda os dados do
 * white label. Sucesso invalida a listagem para o card novo aparecer.
 */
export const createAdminWhiteLabel = async (
  payload: AdminWhiteLabelCreatePayload
): Promise<void> => {
  await api.post(
    `${BASE_URL}${ENDPOINTS.ADMIN_WHITE_LABELS}`,
    payload
  )
}

export const useCreateAdminWhiteLabelMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createAdminWhiteLabel,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] ===
          ADMIN_WHITE_LABELS_QUERY_KEY,
      })
      toast({
        title: "White label criado com sucesso.",
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getApiErrorMessage(
          error,
          "Não foi possível criar o white label."
        ),
        variant: "destructive",
      })
    },
  })
}
